import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    styled,
    CircularProgress,
} from '@mui/material';
import { ActionButton, CancelButton } from '@/components/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DocumentPreview from './DocumentPreview';
import { type FormData } from '../CreateIndigentForm';
import { getIndigentTenantRules } from '../tenant/indigentTenantRules';

const Label = styled(Typography)({
    color: '#49454F',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '0.4px',
    lineHeight: '16px',
});

const Value = styled(Typography)({
    color: '#1B1C1C',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.25px',
    lineHeight: '20px',
    marginTop: '4px',
});

const DocumentLink = styled(Typography)({
    color: '#465E91',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.25px',
    lineHeight: '20px',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginRight: '24px',
    marginBottom: '8px',
});

const StyledDialogActions = styled(DialogActions)({
    padding: '16px 24px',
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    '& > button': {
        width: '169px',
    },
});

interface ReviewDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    formData: FormData;
    isSubmitting?: boolean;
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({ open, onClose, onSubmit, formData, isSubmitting = false }) => {
    const showEmailOnReview = getIndigentTenantRules().emailVisible || Boolean(formData.email?.trim());
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handlePreviewOpen = (file: File) => {
        setPreviewFile(file);
        setPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
        setPreviewFile(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '38px',
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        width: {sm:'35%', xs:'80%'},
                        margin: '0 auto',
                        height: '550px'
                    },
                }}
            >
                <DialogTitle>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            variant='h6'
                            sx={{
                                color: '#132F5F',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '22px',
                                fontWeight: 600,
                                lineHeight: '28px',
                                mb: 1,
                            }}
                        >
                            Submit Indigent
                        </Typography>
                        <Typography
                            sx={{
                                color: '#ABABAB',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '12px',
                                fontWeight: 500,
                                letterSpacing: '0.5px',
                                lineHeight: '16px',
                            }}
                        >
                            Verify your details
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        px: { xs: 3, sm: 1 },
                    }}
                >
                    <Box sx={{ display: 'flex',flexDirection: 'row',
                                justifyContent:'space-between', gap: 4,width: '100%', maxWidth: '416px',mx: 'auto' }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Full Name</Label>
                                <Value>{`${formData.name} ${formData.surname}`}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Personal ID:</Label>
                                <Value>{formData.personalId}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Erf No:</Label>
                                <Value>{formData.erfNo}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Ward No:</Label>
                                <Value>{formData.ward}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Employer Name:</Label>
                                <Value>{formData.employerName || 'N/A'}</Value>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Electricity Meter No:</Label>
                                <Value>{formData.electricityMeterNo}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Account Number:</Label>
                                <Value>{formData.accountNumber}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Financial Year:</Label>
                                <Value>{formData.financialYear}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Monthly Income:</Label>
                                <Value>{formData.income ? `R${formData.income}` : 'N/A'}</Value>
                            </Box>
                            <Box sx={{ mb: 2.5 }}>
                                <Label>Cell Number:</Label>
                                <Value>{formData.cellNumber || 'N/A'}</Value>
                            </Box>
                            {showEmailOnReview && (
                                <Box sx={{ mb: 2.5 }}>
                                    <Label>Email Address:</Label>
                                    <Value>{formData.email?.trim() || 'N/A'}</Value>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{  width: '100%', maxWidth: '416px', mt: 2 }}>
                        <Label>Scanned Documents:</Label>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                flexDirection: 'row',
                                justifyContent:'space-between',
                                mt: 1,
                                width: '100%',
                            }}
                        >
                            {formData.files.map((file: File, index: number) => (
                                <DocumentLink key={index} onClick={() => handlePreviewOpen(file)}>
                                    {file.name}
                                </DocumentLink>
                            ))}
                        </Box>
                    </Box>
                </DialogContent>

                <StyledDialogActions>
                    <CancelButton onClick={onClose} startIcon={<CloseIcon />} disabled={isSubmitting}>
                        Cancel
                    </CancelButton>
                    <ActionButton 
                        onClick={onSubmit} 
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </ActionButton>
                </StyledDialogActions>
            </Dialog>

            {previewFile && (
                <DocumentPreview
                    open={previewOpen}
                    onClose={handlePreviewClose}
                    file={previewFile}
                />
            )}
        </>
    );
};

export default ReviewDialog;
